/**
 * useScheduleHolidays.ts
 * Fetches the statutory holidays for a [startDate, endDate] window so the
 * schedule views can shade non-work days (the lab is closed on stat holidays,
 * same as weekends). Weekends are derived client-side via isWeekend(); holidays
 * are region-specific and must come from the server.
 *
 * Region is intentionally NOT passed — the server defaults it to NB.
 *
 * Best-effort: on fetch failure the hook returns an empty list so the views
 * degrade to weekend-only shading rather than erroring. The window is fetched
 * once per distinct [startDate, endDate]; an AbortController-style cancel flag
 * drops stale responses when the window changes or the component unmounts.
 *
 * Returns the raw holiday list AND a date→name Map for per-cell tooltip hints,
 * so consumers don't each rebuild the lookup.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  fetchHolidaysInRange,
  type HolidayEntry,
} from '../services/scheduleEmployeeSchedule';

export interface ScheduleHolidays {
  /** Raw holiday rows in the window (for plotBands / isStatHoliday). */
  holidays: HolidayEntry[];
  /** ISO date ('YYYY-MM-DD') → holiday name, for tooltip hints + O(1) lookup. */
  nameByDate: Map<string, string>;
}

export function useScheduleHolidays(
  startDate: string,
  endDate: string,
): ScheduleHolidays {
  const [holidays, setHolidays] = useState<HolidayEntry[]>([]);

  useEffect(() => {
    if (!startDate || !endDate) {
      setHolidays([]);
      return;
    }
    let cancelled = false;
    fetchHolidaysInRange(startDate, endDate)
      .then((rows) => {
        if (!cancelled) setHolidays(rows);
      })
      .catch(() => {
        // Best-effort — fall back to weekend-only shading on error.
        if (!cancelled) setHolidays([]);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const nameByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const h of holidays) map.set(h.date.slice(0, 10), h.name);
    return map;
  }, [holidays]);

  return { holidays, nameByDate };
}
